import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import QuotaUpgradeModal from '../components/ui/QuotaUpgradeModal';
import { ArrowLeft, TrendingUp, TrendingDown, Minus, ExternalLink, Share2 } from 'lucide-react';
import ShareModal from '../components/ui/ShareModal';

const DIRECTION_CONFIG = {
  bullish: { label: 'Bullish', icon: TrendingUp, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  bearish: { label: 'Bearish', icon: TrendingDown, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
  neutral: { label: 'Neutral', icon: Minus, color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200' },
};

function getAuthHeader() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short', month: 'long', day: 'numeric', year: 'numeric',
  });
}

function formatReturn(pct) {
  if (pct === null || pct === undefined) return 'N/A';
  const n = parseFloat(pct);
  if (n === 0) return 'N/A';
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
}

export default function PredictionDetailPage() {
  const { id } = useParams();
  const [prediction, setPrediction] = useState(null);
  const [limitError, setLimitError] = useState(null);
  const [quotaError, setQuotaError] = useState(null);
  const [loading, setLoading] = useState(true);
  const isGuest = !localStorage.getItem('token');
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/research/predictions/${id}/`, {
        headers: getAuthHeader(),
      })
      .then((res) => setPrediction(res.data))
      .catch((err) => {
        if (err.response?.status === 402) {
          setQuotaError(err.response.data);
        } else if (err.response?.status === 429) {
          setLimitError(err.response.data);
        } else {
          console.error('Failed to load prediction', err);
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
        <div className="max-w-2xl mx-auto animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-24 mb-6" />
          <div className="bg-white rounded-xl border border-gray-100 p-6 h-64" />
        </div>
      </div>
    );
  }

  if (limitError) {
    return (
      <div className="flex-1 overflow-y-auto bg-gray-50 p-6 flex flex-col">
        <Link
          to="/predictions"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-5 transition-colors max-w-3xl mx-auto w-full"
        >
          <ArrowLeft size={15} />
          Back to Predictions
        </Link>
        <div className="text-center text-gray-500 py-10">
          Rate limit reached. Please try again shortly.
        </div>
      </div>
    );
  }

  if (!prediction) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-400">
        Prediction not found.
      </div>
    );
  }


  const dir = DIRECTION_CONFIG[prediction.direction] || DIRECTION_CONFIG.neutral;
  const DirIcon = dir.icon;
  const returnPct = parseFloat(prediction.current_return_pct ?? 0);
  const returnColor = returnPct > 0 ? 'text-emerald-600' : returnPct < 0 ? 'text-red-600' : 'text-gray-500';

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
      <div className="max-w-2xl mx-auto">
        {/* Back */}
        <Link
          to="/predictions"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-5 transition-colors"
        >
          <ArrowLeft size={15} />
          Back to Predictions
        </Link>

        {/* Main card */}
        <div className="bg-white rounded-xl border border-gray-100 p-6 mb-4">
          {/* Symbol + direction */}
          <div className="flex items-start justify-between mb-4">
            <div>
              <Link
                to={`/analysts/symbols/${prediction.symbol_exchange}/${prediction.symbol_ticker}`}
                className="text-2xl font-bold text-gray-900 hover:text-blue-600 transition-colors"
              >
                {prediction.symbol_ticker}
              </Link>
              <p className="text-sm text-gray-400">{prediction.symbol_name}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsShareModalOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border bg-gray-50 text-gray-600 hover:bg-gray-100 transition-colors"
                title="Share This Call"
              >
                <Share2 size={14} />
              </button>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border ${dir.bg} ${dir.color} ${dir.border}`}>
                <DirIcon size={14} />
                {dir.label}
              </span>
            </div>
          </div>

          {/* Analyst link */}
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 rounded-full bg-gray-100 flex-shrink-0 overflow-hidden flex items-center justify-center text-sm font-semibold text-gray-600">
              {prediction.analyst_image_url ? (
                <img
                  src={prediction.analyst_image_url}
                  alt={prediction.analyst_name}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                prediction.analyst_name?.[0]
              )}
            </div>
            <Link
              to={`/analysts/${prediction.analyst_slug}`}
              className="font-medium text-gray-800 hover:text-gray-900 text-sm"
            >
              {prediction.analyst_name}
            </Link>
            {prediction.platform && (
              <span className="text-xs text-gray-400 capitalize">{prediction.platform}</span>
            )}
            <span className="ml-auto text-xs text-gray-400">{formatDate(prediction.published_at)}</span>
          </div>

          {/* Performance metrics + detail — blurred for guests */}
          <div className="relative">
            {/* Performance metrics */}
            <div className="grid grid-cols-3 gap-4 py-4 border-y border-gray-50 mb-5">
              <div>
                <div className="text-xs text-gray-400 mb-1">Entry Price</div>
                <div className="font-semibold text-gray-800">
                  ${parseFloat(prediction.price_at_prediction).toFixed(2)}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-400 mb-1">Current Price</div>
                <div className="font-semibold text-gray-800">
                  {prediction.current_price
                    ? `$${parseFloat(prediction.current_price).toFixed(2)}`
                    : '—'}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-400 mb-1">Return</div>
                <div className={`font-bold ${returnColor}`}>
                  {formatReturn(prediction.current_return_pct)}
                </div>
              </div>
            </div>

            {/* Price target + timeframe */}
            {(prediction.stated_price_target || prediction.stated_timeframe) && (
              <div className="flex items-center gap-4 text-sm mb-5">
                {prediction.stated_price_target && (
                  <span className="text-gray-500">
                    Target: <span className="font-medium text-gray-800">
                      ${parseFloat(prediction.stated_price_target).toFixed(2)}
                    </span>
                  </span>
                )}
                {prediction.stated_timeframe && (
                  <span className="text-gray-500">
                    Timeframe: <span className="font-medium text-gray-800">{prediction.stated_timeframe}</span>
                  </span>
                )}
              </div>
            )}

            {/* Content excerpt / original quote */}
            {prediction.content && (
              <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700 italic border-l-4 border-gray-300 mb-5">
                "{prediction.content}"
                {prediction.is_copyrighted_source && (
                  <span className="block mt-1 text-xs text-gray-400 not-italic">
                    Paraphrased — original behind paywall
                  </span>
                )}
              </div>
            )}

            {/* Source link */}
            {prediction.source_url && (
              <a
                href={prediction.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                {prediction.source_title || 'View original source'}
                <ExternalLink size={13} />
              </a>
            )}

            {/* Blur overlay for guests */}
            {isGuest && (
              <div className="absolute inset-0 backdrop-blur-sm bg-white/60 rounded-lg flex flex-col items-center justify-center gap-3">
                <p className="text-sm font-medium text-gray-700">Sign in to see full prediction details</p>
                <Link
                  to="/login"
                  className="px-5 py-2 bg-gradient-to-br from-[#4F8EF7] to-[#2c72d9] text-white rounded-lg font-semibold text-sm shadow hover:brightness-110 active:scale-95 transition-all"
                >
                  Sign In
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Author's Reasoning */}
        {prediction.author_reasoning && (
          <div className="bg-white rounded-xl border border-gray-100 p-6 mb-4">
            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">
              Author's Reasoning
            </h2>
            <div className="text-sm text-gray-700 leading-relaxed">
              {prediction.author_reasoning}
            </div>
          </div>
        )}

        <QuotaUpgradeModal
          quotaError={quotaError}
          onClose={() => setQuotaError(null)}
        />

        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          title="Share This Prediction"
          shareUrl={window.location.href}
          imageUrl={prediction.og_image_url || `${API_BASE_URL}/api/research/og/prediction/${prediction.id}/`}
          downloadUrl={`${API_BASE_URL}/api/research/og/prediction/${prediction.id}/?download=true`}
          shareText={`Check out this ${returnPct >= 0 ? '+' : ''}${returnPct.toFixed(1)}% call on ${prediction.symbol_ticker} by ${prediction.analyst_name} on TraderBro!`}
        />
      </div>
    </div>
  );
}
